import React, { useState, useEffect } from "react";
import Modal from "../../../components/modal/index";
import { ResetPasswordAction } from "../../../store/features/auth/authActions";
import { useDispatch } from "react-redux";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ResetPassword = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      setFormData({ oldPassword: "", newPassword: "" });
      setErrors({});
      setIsSubmitting(false);
      setShowPassword({ oldPassword: false, newPassword: false });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = "Must include uppercase, lowercase, and number";
    } else if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      await dispatch(ResetPasswordAction(formData));
      setFormData({ oldPassword: "", newPassword: "" });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="px-1 py-2">
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label
              htmlFor="oldPassword"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPassword.oldPassword ? "text" : "password"}
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-navy-600 dark:text-white ${
                  errors.oldPassword ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your current password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute right-3 top-3.5 text-gray-500 dark:text-gray-400"
                onClick={() => togglePasswordVisibility("oldPassword")}
                disabled={isSubmitting}
              >
                {showPassword.oldPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.oldPassword && (
              <p className="mt-2 text-sm text-red-500">{errors.oldPassword}</p>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="newPassword"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword.newPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-navy-600 dark:text-white ${
                  errors.newPassword ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your new password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute right-3 top-3.5 text-gray-500 dark:text-gray-400"
                onClick={() => togglePasswordVisibility("newPassword")}
                disabled={isSubmitting}
              >
                {showPassword.newPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-2 text-sm text-red-500">{errors.newPassword}</p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Must be at least 8 characters with uppercase, lowercase, and
              number
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-md bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 dark:bg-navy-600 dark:text-white dark:hover:bg-navy-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ResetPassword;
